// The story: quests, story state and arcs, affinity, standing, the event log and NPC memory joined over one condition
// language (decision 0054). The game feeds it deeds (`record`), asks it to talk and give (`talk`, `gift`), steps it
// (`settle`, once a sim step) and reads it back (`objective`, `journal`, facts). Every part is a provider the game
// passes in, so each can be swapped (decision 0055: the quest source, affinity and the event log; the dialogue runner
// is @rpg/dialogue's): the story only calls their interfaces. Deterministic: no clock or random of its own; the game's
// clock comes in through `clock()`.
import { Conditions, type Condition, type FactValue, type FactSource } from "./conditions";
import type { Deed, EventLogProvider, LogEntry } from "./log";
import { standingFacts, type AffinityProvider, type ChangeCause, type NpcMemory, type StandingProvider } from "./social";
import { currentObjectives, deedMatches, type DeedMatch, type ObjectiveDef, type QuestDef, type QuestSource, type QuestState, type QuestStatus, type StoryAction } from "./quests";

/** A rule that moves scores when a deed happens (a defeat of the Warden raises the town's standing). */
export interface DeedRule {
  on: Omit<DeedMatch, "count">;
  affinity?: Record<string, number>;
  standing?: Record<string, number>;
  actions?: readonly StoryAction[];
}

/** A trigger: when its condition holds, its actions run (once, unless `repeat`). */
export interface TriggerDef {
  id: string;
  when: Condition | string;
  repeat?: boolean;
  actions: readonly StoryAction[];
}

/** NPCs who see deeds near them remember them (`memory.<npc>.saw.<kind>` counts up). */
export interface Witnesses {
  radius: number;
  npcs(): readonly string[];
  /** Where an NPC stands now, or null (not around). */
  at(npc: string): [number, number, number] | null;
}

/** What the story is built from. */
export interface StoryOptions {
  quests: QuestSource;
  affinity: AffinityProvider;
  standing: StandingProvider;
  log: EventLogProvider;
  memory: NpcMemory;
  /** The condition language to register the story's facts on (default a new one); the game adds its own sources. */
  conditions?: Conditions;
  /** Story variables and their starting values (`story.<id>` facts; only declared ones can be set). */
  vars?: Record<string, FactValue>;
  /** Arcs: their beats in order (`arc.<id>` facts; each starts on its first beat). */
  arcs?: Record<string, readonly string[]>;
  triggers?: readonly TriggerDef[];
  deedRules?: readonly DeedRule[];
  /** The deed kinds the game records (`log.<kind>` facts are known for these). */
  deedKinds: readonly string[];
  /** Talking: the affinity it adds, at most once per NPC a day. */
  talk?: { affinity: number };
  witnesses?: Witnesses;
  /** The story clock (seconds) and the day. */
  clock(): number;
  day?(): number;
  /** Actions only the game can do (`{ host: "give", args: { item, count } }`), and their names (for checks). */
  host?(name: string, args: Record<string, FactValue>): void;
  hosts?: readonly string[];
}

/** A quest's status changed, or its current objective did. */
export interface QuestChange {
  quest: string;
  state: Exclude<QuestStatus, "none">;
  /** The current objective after the change (the first, for a parallel quest), or null. */
  objective: string | null;
  /** The objective just completed, if that was the change. */
  completed: string | null;
}

/** The marker the HUD points at: the tracked quest's current objective. */
export interface ObjectiveInfo {
  quest: string;
  objective: string;
  label: string;
  marker: string | null;
}

/** A journal entry (the quest log). */
export interface JournalEntry {
  quest: string;
  kind: string;
  title: string;
  summary: string | null;
  giver: string | null;
  status: Exclude<QuestStatus, "none">;
  objectives: { id: string; label: string; done: boolean; current: boolean; count: number; need: number }[];
  tracked: boolean;
}

/** The story's own state (the providers keep theirs). */
interface OwnState {
  quests: Record<string, QuestState>;
  vars: Record<string, FactValue>;
  arcs: Record<string, string>;
  fired: string[];
  tracked: string | null;
}

/** The story. */
export interface Story {
  readonly conditions: Conditions;
  readonly quests: QuestSource;
  readonly affinity: AffinityProvider;
  readonly standing: StandingProvider;
  readonly log: EventLogProvider;
  readonly memory: NpcMemory;
  /** Record a deed: the log, witnesses, deed rules, objectives counting it; then settle. */
  record(deed: Deed): LogEntry;
  /** Run actions (from a quest, ink, a trigger); then settle. */
  apply(actions: readonly StoryAction[], cause?: ChangeCause): void;
  /** Check conditions: quests offered, objectives done, quests failed, triggers. Call once a step. */
  settle(): void;
  /** A quest's status. */
  status(quest: string): QuestStatus;
  /** The player talked with an NPC (memory, the log, the day's talk affinity). */
  talk(npc: string): void;
  /** The player gave an NPC an item: the affinity change it made. */
  gift(npc: string, item: string): number;
  /** Track a quest for the marker (null: the default, the first active main quest, else the first active one). */
  track(quest: string | null): void;
  objective(): ObjectiveInfo | null;
  journal(): JournalEntry[];
  /** A story variable's value; setting one not declared throws. */
  get(name: string): FactValue;
  set(name: string, value: FactValue): void;
  /** Call `fn` on every quest change. Returns an unsubscribe. */
  onQuest(fn: (c: QuestChange) => void): () => void;
  /** Problems in the story's content: conditions naming unknown facts, actions naming unknown quests, NPCs, factions,
   *  variables, arcs or host actions. Empty when sound. */
  check(extra?: { conditions?: readonly (Condition | string)[] }): string[];
  /** Counts up on every change (the game re-snapshots its hash copy when it moves). */
  version(): number;
  snapshot(): StorySnapshot;
  restore(s: StorySnapshot): void;
}

/** Everything the story and its standard providers keep, JSON-safe. */
export interface StorySnapshot {
  own: OwnState;
  affinity: unknown;
  standing: unknown;
  log: unknown;
  memory: unknown;
}

const varName = (name: string): string => (name.startsWith("story.") ? name.slice(6) : name);

/** Build the story over its providers. Registers its facts on the conditions: `quest.<id>` (status),
 *  `quest.<id>.objective`, `objective.<quest>.<objective>` (done), `story.<var>`, `arc.<id>`, `affinity.<npc>` and
 *  `.tier`, `standing.<faction>`, `.tier` and `.<perk>`, `log.<kind>` and `log.<kind>.<subject>` (lifetime counts),
 *  `memory.<npc>.<key>`. */
export function createStory(o: StoryOptions): Story {
  const conditions = o.conditions ?? new Conditions();
  const declared = { ...(o.vars ?? {}) };
  const arcs = o.arcs ?? {};
  const fresh = (): OwnState => ({ quests: {}, vars: structuredClone(declared), arcs: Object.fromEntries(Object.entries(arcs).map(([id, b]) => [id, b[0] ?? ""])), fired: [], tracked: null });
  let s = fresh();
  let ver = 0;
  const bump = (): void => void ver++;
  const listeners = new Set<(c: QuestChange) => void>();
  const pending: QuestChange[] = [];

  const def = (id: string): QuestDef => {
    const q = o.quests.get(id);
    if (!q) throw new Error(`story: no quest "${id}"`);
    return q;
  };
  const qs = (id: string): QuestState => (s.quests[id] ??= { status: "none", done: [], counts: {}, since: o.clock() });
  const current = (q: QuestDef): ObjectiveDef[] => currentObjectives(q, s.quests[q.id]);
  const notify = (quest: string, completed: string | null): void => {
    const st = s.quests[quest]!;
    if (st.status === "none") return;
    pending.push({ quest, state: st.status, objective: current(def(quest))[0]?.id ?? null, completed });
  };

  // --- facts -------------------------------------------------------------------------------------------------------
  const knownQuest = (id: string): boolean => o.quests.get(id) !== undefined;
  const sources: FactSource[] = [
    {
      name: "quest",
      get: (k) => {
        const [id, sub] = k.split(".", 2) as [string, string | undefined];
        const st = s.quests[id];
        if (sub === "objective") return current(def(id))[0]?.id ?? "none";
        return st?.status ?? "none";
      },
      knows: (k) => {
        const [id, sub, more] = k.split(".");
        return !!id && knownQuest(id) && (sub === undefined || sub === "objective") && more === undefined;
      },
      keys: () => o.quests.list().map((q) => q.id),
    },
    {
      name: "objective",
      get: (k) => {
        const i = k.indexOf(".");
        return s.quests[k.slice(0, i)]?.done.includes(k.slice(i + 1)) ?? false;
      },
      knows: (k) => {
        const i = k.indexOf(".");
        const q = i > 0 ? o.quests.get(k.slice(0, i)) : undefined;
        return !!q && q.objectives.some((x) => x.id === k.slice(i + 1));
      },
    },
    { name: "story", get: (k) => s.vars[k] ?? null, knows: (k) => k in declared, keys: () => Object.keys(declared) },
    { name: "arc", get: (k) => s.arcs[k] ?? null, knows: (k) => k in arcs, keys: () => Object.keys(arcs) },
    {
      name: "affinity",
      get: (k) => (k.endsWith(".tier") ? o.affinity.tier(k.slice(0, -5)).id : o.affinity.get(k)),
      knows: (k) => o.affinity.npcs().includes(k.endsWith(".tier") ? k.slice(0, -5) : k),
      keys: () => o.affinity.npcs(),
    },
    standingFacts(o.standing),
    {
      name: "log",
      get: (k) => {
        const i = k.indexOf(".");
        return i < 0 ? o.log.total(k) : o.log.total(k.slice(0, i), k.slice(i + 1));
      },
      knows: (k) => o.deedKinds.includes(k.split(".")[0]!),
      keys: () => [...o.deedKinds],
    },
    {
      name: "memory",
      get: (k) => {
        const i = k.indexOf(".");
        return o.memory.get(k.slice(0, i), k.slice(i + 1));
      },
      knows: (k) => {
        const i = k.indexOf(".");
        return i > 0 && npcs().includes(k.slice(0, i));
      },
    },
  ];
  const npcs = (): string[] => [...new Set([...o.affinity.npcs(), ...(o.witnesses?.npcs() ?? [])])];
  for (const src of sources) conditions.register(src);

  // --- actions -----------------------------------------------------------------------------------------------------
  const setQuest = (id: string, status: Exclude<QuestStatus, "none">): void => {
    const q = def(id);
    const st = qs(id);
    if (st.status === status) return;
    // a quest moves forward only: offered → active → done | failed (offering an active quest does nothing)
    const order: Record<QuestStatus, number> = { none: 0, offered: 1, active: 2, done: 3, failed: 3 };
    if (order[status] <= order[st.status]) return;
    st.status = status;
    st.since = o.clock();
    bump();
    o.log.record({ kind: "quest", subject: id, data: { state: status } }, o.clock());
    notify(id, null);
    if (status === "offered" && q.autoStart) setQuest(id, "active");
    else if (status === "active") {
      run(q.start ?? []);
      for (const ob of current(q)) run(ob.start ?? []);
    } else if (status === "done") run(q.rewards ?? []);
    else if (status === "failed") run(q.failed ?? []);
  };
  const completeObjective = (q: QuestDef, ob: ObjectiveDef): void => {
    const st = qs(q.id);
    if (st.done.includes(ob.id)) return;
    const before = new Set(current(q).map((x) => x.id));
    st.done.push(ob.id);
    bump();
    run(ob.done ?? []);
    o.log.record({ kind: "objective", subject: `${q.id}.${ob.id}` }, o.clock());
    if (st.done.length >= q.objectives.length) {
      notify(q.id, ob.id);
      setQuest(q.id, "done");
      return;
    }
    for (const next of current(q)) if (!before.has(next.id)) run(next.start ?? []);
    notify(q.id, ob.id);
  };
  const run = (actions: readonly StoryAction[], cause: ChangeCause = { kind: "story" }): void => {
    for (const a of actions) {
      if ("set" in a) story.set(a.set, a.value);
      else if ("add" in a) {
        const v = story.get(a.add);
        story.set(a.add, (typeof v === "number" ? v : 0) + a.amount);
      } else if ("quest" in a) setQuest(a.quest, a.state);
      else if ("affinity" in a) {
        o.affinity.change(a.affinity, a.amount, cause);
        bump();
      } else if ("standing" in a) {
        o.standing.change(a.standing, a.amount, cause);
        bump();
      } else if ("remember" in a) {
        o.memory.set(a.remember, a.key, a.value);
        bump();
      } else if ("arc" in a) {
        if (!(a.arc in arcs)) throw new Error(`story: no arc "${a.arc}"`);
        if (!arcs[a.arc]!.includes(a.beat)) throw new Error(`story: arc "${a.arc}" has no beat "${a.beat}"`);
        s.arcs[a.arc] = a.beat;
        bump();
      } else if ("host" in a) {
        if (!o.host) throw new Error(`story: no host for "${a.host}"`);
        o.host(a.host, a.args ?? {});
        bump();
      }
    }
  };
  const flush = (): void => {
    while (pending.length) {
      const c = pending.shift()!;
      for (const fn of listeners) fn(c);
    }
  };

  const settleOnce = (): boolean => {
    const v0 = ver;
    for (const q of o.quests.list()) {
      const st = s.quests[q.id];
      const status = st?.status ?? "none";
      if (status === "none" && q.offer !== undefined && conditions.test(q.offer)) setQuest(q.id, "offered");
      else if (status === "active") {
        if (q.fail !== undefined && conditions.test(q.fail)) {
          setQuest(q.id, "failed");
          continue;
        }
        for (const ob of current(q)) if (ob.when !== undefined && conditions.test(ob.when)) completeObjective(q, ob);
      }
    }
    for (const tr of o.triggers ?? []) {
      if (!tr.repeat && s.fired.includes(tr.id)) continue;
      if (!conditions.test(tr.when)) continue;
      if (!s.fired.includes(tr.id)) s.fired.push(tr.id);
      bump();
      run(tr.actions, { kind: "trigger", detail: tr.id });
    }
    return ver !== v0;
  };

  const story: Story = {
    conditions,
    quests: o.quests,
    affinity: o.affinity,
    standing: o.standing,
    log: o.log,
    memory: o.memory,
    record(deed) {
      const t = o.clock();
      const e = o.log.record(deed, t);
      bump();
      if (deed.pos && o.witnesses) {
        for (const npc of o.witnesses.npcs()) {
          const at = o.witnesses.at(npc);
          if (at && Math.hypot(at[0] - deed.pos[0], at[1] - deed.pos[1], at[2] - deed.pos[2]) <= o.witnesses.radius) o.memory.add(npc, `saw.${deed.kind}`, 1);
        }
      }
      for (const r of o.deedRules ?? []) {
        if (!deedMatches({ ...r.on }, deed)) continue;
        const cause = { kind: "deed", detail: `${deed.kind}:${deed.subject}` };
        for (const [npc, n] of Object.entries(r.affinity ?? {})) o.affinity.change(npc, n, cause);
        for (const [f, n] of Object.entries(r.standing ?? {})) o.standing.change(f, n, cause);
        run(r.actions ?? [], cause);
      }
      for (const q of o.quests.list()) {
        for (const ob of current(q)) {
          if (!ob.on || !deedMatches(ob.on, deed)) continue;
          const st = qs(q.id);
          st.counts[ob.id] = (st.counts[ob.id] ?? 0) + 1;
          if (st.counts[ob.id]! >= (ob.on.count ?? 1)) completeObjective(q, ob);
        }
      }
      story.settle();
      return e;
    },
    apply(actions, cause) {
      run(actions, cause);
      story.settle();
    },
    settle() {
      // a change can make another condition true (an objective done offers a quest): settle until nothing moves, at
      // most a few rounds (a content loop shows as a throw, not a hang)
      for (let i = 0; i < 16; i++) if (!settleOnce()) break;
      flush();
    },
    status: (quest) => (def(quest), s.quests[quest]?.status ?? "none"),
    talk(npc) {
      const day = o.day?.() ?? 0;
      const met = o.memory.get(npc, "met") === true;
      o.memory.set(npc, "met", true);
      o.memory.add(npc, "talks", 1);
      const lastDay = o.memory.get(npc, "talkDay");
      if (o.talk && o.affinity.npcs().includes(npc) && (lastDay !== day || !met)) o.affinity.change(npc, o.talk.affinity, { kind: "talk" });
      o.memory.set(npc, "talkDay", day);
      story.record({ kind: "talked", subject: npc });
    },
    gift(npc, item) {
      const d = o.affinity.gift(npc, item, o.day?.() ?? 0);
      o.memory.set(npc, "lastGift", item);
      story.record({ kind: "gift", subject: npc, data: { item, change: d } });
      return d;
    },
    track(quest) {
      if (quest !== null) def(quest);
      s.tracked = quest;
      bump();
    },
    objective() {
      const active = o.quests.list().filter((q) => s.quests[q.id]?.status === "active");
      const q = (s.tracked ? active.find((x) => x.id === s.tracked) : undefined) ?? active.find((x) => x.kind === "main") ?? active[0];
      const ob = q ? current(q)[0] : undefined;
      return q && ob ? { quest: q.id, objective: ob.id, label: ob.label, marker: ob.marker ?? null } : null;
    },
    journal() {
      const tracked = story.objective()?.quest ?? null;
      const out: JournalEntry[] = [];
      for (const q of o.quests.list()) {
        const st = s.quests[q.id];
        if (!st || st.status === "none") continue;
        const cur = new Set(current(q).map((x) => x.id));
        out.push({
          quest: q.id,
          kind: q.kind,
          title: q.title,
          summary: q.summary ?? null,
          giver: q.giver ?? null,
          status: st.status,
          objectives: q.objectives
            .filter((x) => st.done.includes(x.id) || cur.has(x.id))
            .map((x) => ({ id: x.id, label: x.label, done: st.done.includes(x.id), current: cur.has(x.id), count: st.counts[x.id] ?? 0, need: x.on?.count ?? 1 })),
          tracked: tracked === q.id,
        });
      }
      return out;
    },
    get(name) {
      const k = varName(name);
      if (!(k in declared)) throw new Error(`story: no variable "${k}" (declare it in the story's vars)`);
      return s.vars[k] ?? null;
    },
    set(name, value) {
      const k = varName(name);
      if (!(k in declared)) throw new Error(`story: no variable "${k}" (declare it in the story's vars)`);
      if (s.vars[k] === value) return;
      s.vars[k] = value;
      bump();
    },
    onQuest(fn) {
      listeners.add(fn);
      return () => void listeners.delete(fn);
    },
    check(extra = {}) {
      const issues: string[] = [];
      const cond = (where: string, c: Condition | string | undefined): void => {
        if (c === undefined) return;
        for (const p of conditions.check(c)) issues.push(`${where}: ${p}`);
      };
      const acts = (where: string, list: readonly StoryAction[] | undefined): void => {
        for (const a of list ?? []) {
          if ("set" in a || "add" in a) {
            const k = varName("set" in a ? a.set : a.add);
            if (!(k in declared)) issues.push(`${where}: no story variable "${k}"`);
          } else if ("quest" in a && !knownQuest(a.quest)) issues.push(`${where}: no quest "${a.quest}"`);
          else if ("affinity" in a && !o.affinity.npcs().includes(a.affinity)) issues.push(`${where}: no affinity for "${a.affinity}"`);
          else if ("standing" in a && !o.standing.factions().includes(a.standing)) issues.push(`${where}: no faction "${a.standing}"`);
          else if ("remember" in a && !npcs().includes(a.remember)) issues.push(`${where}: no NPC "${a.remember}"`);
          else if ("arc" in a && !(arcs[a.arc] ?? []).includes(a.beat)) issues.push(`${where}: no arc beat "${a.arc}/${a.beat}"`);
          else if ("host" in a && o.hosts && !o.hosts.includes(a.host)) issues.push(`${where}: no host action "${a.host}"`);
        }
      };
      for (const q of o.quests.list()) {
        const w = `quest ${q.id}`;
        cond(`${w} offer`, q.offer);
        cond(`${w} fail`, q.fail);
        acts(`${w} start`, q.start);
        acts(`${w} rewards`, q.rewards);
        acts(`${w} failed`, q.failed);
        for (const ob of q.objectives) {
          cond(`${w}/${ob.id}`, ob.when);
          if (ob.on && !o.deedKinds.includes(ob.on.kind)) issues.push(`${w}/${ob.id}: no deed kind "${ob.on.kind}"`);
          acts(`${w}/${ob.id} start`, ob.start);
          acts(`${w}/${ob.id} done`, ob.done);
        }
      }
      for (const t of o.triggers ?? []) {
        cond(`trigger ${t.id}`, t.when);
        acts(`trigger ${t.id}`, t.actions);
      }
      for (const r of o.deedRules ?? []) {
        if (!o.deedKinds.includes(r.on.kind)) issues.push(`deed rule: no deed kind "${r.on.kind}"`);
        for (const n of Object.keys(r.affinity ?? {})) if (!o.affinity.npcs().includes(n)) issues.push(`deed rule ${r.on.kind}: no affinity for "${n}"`);
        for (const f of Object.keys(r.standing ?? {})) if (!o.standing.factions().includes(f)) issues.push(`deed rule ${r.on.kind}: no faction "${f}"`);
        acts(`deed rule ${r.on.kind}`, r.actions);
      }
      for (const [i, c] of (extra.conditions ?? []).entries()) cond(`condition ${i}`, c);
      return issues;
    },
    version: () => ver,
    snapshot: () => ({ own: structuredClone(s), affinity: o.affinity.snapshot(), standing: o.standing.snapshot(), log: o.log.snapshot(), memory: o.memory.snapshot() }),
    restore(snap) {
      if (!snap || typeof snap.own !== "object") throw new Error("story: not a story snapshot");
      s = { ...fresh(), ...structuredClone(snap.own), vars: { ...structuredClone(declared), ...structuredClone(snap.own.vars) } };
      o.affinity.restore(snap.affinity);
      o.standing.restore(snap.standing);
      o.log.restore(snap.log);
      o.memory.restore(snap.memory);
      pending.length = 0;
      bump();
    },
  };
  return story;
}
